import { EditorSection } from '@/components/plot/editor/EditorSection';
import { FigurePanel, useFigureCompositionStore } from '@/store/figure-composition';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Replace, Edit2, Trash2 } from 'lucide-react';
import { PlotSelector } from '../PlotSelector';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { useState, useEffect } from 'react';
import { usePlotsStore } from '@/store/plots';

interface PanelContentTabProps {
  panel: FigurePanel;
}

export function PanelContentTab({ panel }: PanelContentTabProps) {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const updatePanel = useFigureCompositionStore((state) => state.updatePanel);
  const { go } = useNav();
  const [showPlotSelector, setShowPlotSelector] = useState(false);
  const plots = usePlotsStore((state) => state.plots);
  const loadPlots = usePlotsStore((state) => state.loadPlots);
  const [plotTitle, setPlotTitle] = useState<string>('');

  // Load plots if not already loaded
  useEffect(() => {
    if (currentComposition?.projectId && plots.length === 0) {
      loadPlots(currentComposition.projectId).catch(() => {
        // Silently fail - plots might not be needed immediately
      });
    }
  }, [currentComposition?.projectId, plots.length, loadPlots]);

  useEffect(() => {
    if (panel.plotId) {
      const plot = plots.find((p) => p.id === panel.plotId);
      setPlotTitle(plot?.title || panel.plotId);
    } else {
      setPlotTitle('');
    }
  }, [panel.plotId, plots]);

  if (!currentComposition) return null;

  const handleEditPlot = () => {
    if (!panel.plotId || !currentComposition.projectId) return;
    go(routes.plotEditor.open(currentComposition.projectId, panel.plotId));
  };

  const handleReplacePlot = () => {
    setShowPlotSelector(true);
  };

  const handleRemovePlot = () => {
    updatePanel(panel.id, { plotId: '' });
  };

  const handleSelectPlot = (plotId: string, _plotVersionId?: string) => {
    // Always use latest version - ignore plotVersionId from selector to ensure fresh data
    updatePanel(panel.id, { plotId, plotVersionId: undefined });
    setShowPlotSelector(false);
  };

  return (
    <div className="space-y-4">
      <EditorSection
        id="panel-content"
        title="Panel content"
        description="Choose which plot appears in this panel."
      >
        <div className="space-y-2">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Plot</Label>
            <div className="text-sm text-muted-foreground">
              {panel.plotId ? plotTitle : 'No plot assigned'}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleReplacePlot}
              title="Replace plot"
            >
              <Replace className="h-3 w-3" />
            </Button>
            {panel.plotId && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={handleEditPlot}
                  title="Edit plot"
                >
                  <Edit2 className="h-3 w-3" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 text-destructive"
                  onClick={handleRemovePlot}
                  title="Remove plot"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
          <PlotSelector
            projectId={currentComposition.projectId}
            isOpen={showPlotSelector}
            onSelect={handleSelectPlot}
            onClose={() => setShowPlotSelector(false)}
          />
        </div>
      </EditorSection>
    </div>
  );
}

